import React from 'react';
import { useLocation } from 'react-router-dom';
import { useRecentlyViewed } from './use-recently-viewed';
import { FavouritedData, FavouritedResponse } from '@atlassian/cssxp-typescommon';
import { useApplicationContext } from '../../common-context';

export const useFavorites = () => {
  const location = useLocation();
  const url = location?.pathname;
  const [isFavorited, setIsFavorited] = React.useState(false);
  const key = 'favourites_';
  let maxPerAppCount: number;
  let appName: string;
  let maxPerEntityCount: number;
  //to set the data in the local storage with required parameters
  const { init, updateRecentlyViewed } = useRecentlyViewed();
  const context = useApplicationContext();
  if (context.uiConfig && context.uiConfig.favouritePages.maxPerAppCount) {
    maxPerAppCount = context.uiConfig.favouritePages.maxPerAppCount;
  }
  if (context.product) {
    appName = context.product;
  }
  React.useEffect(() => {
    let parsedFavorites;
    try {
      const favorites = localStorage.getItem(`${key}${appName}`);
      parsedFavorites = favorites ? JSON.parse(favorites) : [];
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching favorite pages');
    }
    const hasURL = parsedFavorites?.some((item: any) => item.url === url);
    setIsFavorited(hasURL);
  }, [url]);

  const handleFavoriteToggle = (entityName?: string, metadata?: any) => {
    const pageTitle = document.title;
    if (isFavorited) {
      removeFavorites(url, entityName);
    } else {
      init({ appName, maxPerAppCount, maxPerEntityCount });
      //if not already favorited add to the local storage
      updateRecentlyViewed({ pageTitle, key, entityName, metadata });
    }
    setIsFavorited(!isFavorited);
  };

  const getFavourites = (entityName?: string): FavouritedResponse => {
    let hasErrorGettingAppData = false;
    let hasErrorGettingEntityData = false;
    if (entityName) {
      let parsedEntityData: FavouritedData[] = [];
      try {
        const entityData = localStorage.getItem(`${key}${appName}_${entityName}`);
        parsedEntityData = entityData ? JSON.parse(entityData) : [];
      } catch (error) {
        hasErrorGettingEntityData = true;
      }
      parsedEntityData.sort((a, b) => b.timestamp - a.timestamp);
      return { favouritedData: parsedEntityData, hasError: hasErrorGettingEntityData };
    }
    let parsedAppData: FavouritedData[] = [];
    try {
      const appData = localStorage.getItem(`${key}${appName}`);
      parsedAppData = appData ? JSON.parse(appData) : [];
    } catch (error) {
      hasErrorGettingAppData = true;
    }
    parsedAppData.sort((a, b) => b.timestamp - a.timestamp);
    return { favouritedData: parsedAppData, hasError: hasErrorGettingAppData };
  };

  const removeFavorites = (url: string, entityName?: string) => {
    if (entityName) {
      const parsedEntityData = getFavourites(entityName).favouritedData;
      const index = parsedEntityData.findIndex((favorite: any) => favorite.url === url);
      if (index >= 0) {
        try {
          parsedEntityData.splice(index, 1);
          localStorage.setItem(`${key}${appName}_${entityName}`, JSON.stringify(parsedEntityData));
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error removing favorites page for', `${entityName}`);
        }
      }
    }

    const parsedAppData = getFavourites().favouritedData;
    const index = parsedAppData.findIndex((favorite: any) => favorite.url === url);
    if (index >= 0) {
      try {
        parsedAppData.splice(index, 1);
        localStorage.setItem(`${key}${appName}`, JSON.stringify(parsedAppData));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error removing favorites page');
      }
    }
    setIsFavorited(!isFavorited);
  };
  return { isFavorited, handleFavoriteToggle, useFavorites, getFavourites, removeFavorites };
};
